import React, {useState, useRef} from 'react';
import {Modal} from '@carbon/react';
import {CloudUpload, TrashCan, WarningFilled, Download, Launch, InformationFilled, ErrorFilled, Error as ErrorIcon, Close} from '@carbon/icons-react';
import {ReactComponent as XlsxIcon} from './xlsx-icon.svg';
import './upload-files-modal.scss';

type SelectedFile = {
    id: string;
    file: File;
    error?: string;
};

export type RequirementRow = {
    requirement: string;
    details: React.ReactNode;
    subRow?: boolean;
};

export type Props = {
    open: boolean;
    onClose: () => void;
    onUpload: (files: File[]) => void;
    existingFileNames?: string[];
    uploadingFileNames?: string[];
};

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];

export const ACCEPTED_MIME = new Set([
    'text/csv',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
]);

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB

const COLUMN_NAMES = [
    'NPI', 'Provider/Facility Name', 'Facility or Provider (F or P)',
    'Provider Degree', 'Taxonomy Code', 'Specialty Code', 'Specialty',
    'Group/Service Location Name', 'Street Address', 'City', 'County', 'State', 'Zip code',
];

export const REQUIREMENTS: RequirementRow[] = [
    {requirement: 'File type', details: 'CSV or Excel (.xlsx)'},
    {
        requirement: 'Column Order and Naming Convention',
        details: (
            <span>
                Columns should be named as specified:
                <ul className="upload-requirements__column-list">
                    {COLUMN_NAMES.map(col => <li key={col}>{col}</li>)}
                </ul>
            </span>
        ),
    },
    {requirement: 'NPI Format', details: 'Numeric, exactly 10 digits; no letters or special characters', subRow: true},
    {requirement: 'Facility or Provider Indicator', details: "Single character, field entry must be either 'F' or 'P'", subRow: true},
    {requirement: 'Provider Degree', details: 'Standardize abbreviations used in this field; MD, DO, NP, PA, APRN, etc.', subRow: true},
    {requirement: 'Taxonomy Code', details: 'Ensure the taxonomy code entered matches official taxonomy codes (10 characters, alpha-numeric)', subRow: true},
    {requirement: 'Zip Code', details: '5-digit or ZIP+5 numeric format', subRow: true},
    {requirement: 'Flag critical data', details: "Flag any blank fields for NPI, Provider/Facility Name, or Address with 'NA'"},
    {
        requirement: 'Consistent Naming and Standardization',
        details: 'Provider/Facility, city, county, and state entry fields must be consistently formatted; no extra spaces and correct capitalization',
    },
    {
        requirement: 'Multiple Locations',
        details: 'If a provider practices the same specialty at multiple locations, each location should be listed on a separate row with the new address and all other fields duplicated',
    },
];

const isValidType = (file: File): boolean => {
    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
    return ACCEPTED_EXTENSIONS.includes(ext) || ACCEPTED_MIME.has(file.type);
};

const getFileError = (file: File): string | undefined => {
    if (!isValidType(file))
        return `Unsupported file type. Only ${ACCEPTED_EXTENSIONS.join(', ')} files are accepted.`;
    if (file.size === 0)
        return 'File is empty and cannot be uploaded.';
    if (file.size > MAX_FILE_SIZE)
        return `File exceeds the 2 GB size limit (${formatBytes(file.size)}).`;
    return undefined;
};


const stripExt = (name: string) => name.replace(/\.[^.]+$/, '').toLowerCase();

export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getFileExtension = (file: File): string =>
    (file.name.split('.').pop() ?? '').toLowerCase();

export const getFileTypeName = (file: File): string => {
    const ext = getFileExtension(file);
    if (ext === 'xlsx' || ext === 'xls') return 'Microsoft Excel (.xlsx)';
    if (ext === 'csv') return 'CSV File (.csv)';
    return file.type || 'Unknown';
};

type DupWarning = {systemDups: string[]; listDups: string[]; totalAdded: number};

const UploadFilesModal = ({open, onClose, onUpload, existingFileNames = [], uploadingFileNames = []}: Props) => {
    const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isDragInvalid, setIsDragInvalid] = useState(false);
    const [dropError, setDropError] = useState<{count: number; firstName: string} | null>(null);
    const [dupWarning, setDupWarning] = useState<DupWarning | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const replaceTargetId = useRef<string | null>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    const addFiles = (incoming: FileList | File[]) => {
        const fileArray = Array.from(incoming);
        const systemSet = new Set(existingFileNames.map(stripExt));
        const listSet = new Set([
            ...selectedFiles.map(f => stripExt(f.file.name)),
            ...uploadingFileNames.map(stripExt),
        ]);
        const listDups = fileArray.filter(f => listSet.has(stripExt(f.name))).map(f => f.name);
        const systemDups = fileArray
            .filter(f => systemSet.has(stripExt(f.name)) && !listSet.has(stripExt(f.name)))
            .map(f => f.name);
        const nonDuplicates = fileArray.filter(
            f => !systemSet.has(stripExt(f.name)) && !listSet.has(stripExt(f.name))
        );
        setDupWarning(
            systemDups.length > 0 || listDups.length > 0
                ? {systemDups, listDups, totalAdded: fileArray.length}
                : null
        );
        setSelectedFiles(prev => {
            const existing = new Set(prev.map(f => `${f.file.name}:${f.file.size}`));
            const toAdd = nonDuplicates
                .filter(f => !existing.has(`${f.name}:${f.size}`))
                .map(file => ({
                    id: crypto.randomUUID(),
                    file,
                    error: getFileError(file),
                }));
            return [...prev, ...toAdd];
        });
    };

    const removeFile = (id: string) => setSelectedFiles(prev => prev.filter(f => f.id !== id));

    const handleReplace = (id: string) => {
        replaceTargetId.current = id;
        replaceInputRef.current?.click();
    };

    const handleReplaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetId = replaceTargetId.current;
        if (!e.target.files?.length || !targetId) return;
        const newFile = e.target.files[0];
        setSelectedFiles(prev =>
            prev.map(f =>
                f.id === targetId
                    ? {id: f.id, file: newFile, error: getFileError(newFile)}
                    : f
            )
        );
        replaceTargetId.current = null;
        e.target.value = '';
    };

    const hasInvalidDragItems = (items: DataTransferItemList): boolean =>
        Array.from(items).some(item => item.kind === 'file' && !!item.type && !ACCEPTED_MIME.has(item.type));

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        if (dropZoneRef.current?.contains(e.relatedTarget as Node)) return;
        const invalid = hasInvalidDragItems(e.dataTransfer.items);
        setIsDragInvalid(invalid);
        setIsDragging(!invalid);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        if (dropZoneRef.current?.contains(e.relatedTarget as Node)) return;
        setIsDragging(false);
        setIsDragInvalid(false);
    };

    const handleDragOver = (e: React.DragEvent) => e.preventDefault();

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        setIsDragInvalid(false);
        const all = Array.from(e.dataTransfer.files);
        const typeInvalid = all.filter(f => !isValidType(f));
        const typeValid = all.filter(isValidType);
        if (typeInvalid.length > 0) setDropError({count: typeInvalid.length, firstName: typeInvalid[0].name});
        if (typeValid.length > 0) addFiles(typeValid);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addFiles(e.target.files);
            e.target.value = '';
        }
    };

    const handleClose = () => {
        setSelectedFiles([]);
        setDropError(null);
        setDupWarning(null);
        setIsDragging(false);
        setIsDragInvalid(false);
        onClose();
    };

    const handleSubmit = () => {
        const valid = selectedFiles.filter(f => !f.error);
        if (valid.length === 0) return;
        onUpload(valid.map(f => f.file));
        handleClose();
    };

    const validFiles = selectedFiles.filter(f => !f.error);
    const totalSize = validFiles.reduce((sum, f) => sum + f.file.size, 0);

    return (
        <Modal
            open={open}
            onRequestClose={handleClose}
            modalHeading="Upload Files"
            primaryButtonText="Upload Files"
            secondaryButtonText="Cancel"
            primaryButtonDisabled={validFiles.length === 0}
            onRequestSubmit={handleSubmit}
            onSecondarySubmit={handleClose}
            size="md"
            className="upload-files-modal-container"
            hasScrollingContent
        >
            <div className="upload-files-modal">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    multiple
                    className="upload-hidden-input"
                    onChange={handleFileInputChange}
                />
                <input
                    ref={replaceInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    className="upload-hidden-input"
                    onChange={handleReplaceChange}
                />

                <p className="upload-modal-subtitle">
                    Upload one or more roster files to get started. Processing begins automatically. You'll be notified when files are ready, and can check their status anytime.
                </p>

                {dropError !== null && (
                    <div className="upload-drop-error" role="alert">
                        <ErrorFilled size={16} className="upload-drop-error__icon" />
                        <div className="upload-drop-error__content">
                            <p className="upload-drop-error__title">
                                {dropError.count === 1 ? '1 file wasn\'t added' : `${dropError.count} files weren't added`}
                            </p>
                            <p className="upload-drop-error__body">
                                {dropError.count === 1
                                    ? `${dropError.firstName} is not supported. Upload .csv or .xlsx files only`
                                    : 'Some files are not supported. Upload .csv or .xlsx files only'}
                            </p>
                        </div>
                        <div className="upload-drop-error__actions">
                            <button
                                type="button"
                                className="upload-drop-error__action-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Browse files
                            </button>
                            <button
                                type="button"
                                className="upload-drop-error__action-btn"
                                onClick={() => setDropError(null)}
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                )}

                {dupWarning !== null && (() => {
                    const {systemDups, listDups, totalAdded} = dupWarning;
                    const totalBlocked = systemDups.length + listDups.length;
                    const nonDuplicateCount = totalAdded - totalBlocked;
                    const hasBoth = systemDups.length > 0 && listDups.length > 0;

                    let title: string;
                    if (nonDuplicateCount > 0) {
                        title = `${nonDuplicateCount} file${nonDuplicateCount !== 1 ? 's' : ''} added`;
                    } else if (totalBlocked === 1) {
                        title = 'Duplicate file not added';
                    } else if (systemDups.length === 0) {
                        title = `${listDups.length} duplicate files were not added`;
                    } else {
                        title = 'Duplicate files not added';
                    }

                    const fileList = (names: string[]) => (
                        <ul className="upload-dup-warning__file-list">
                            {names.map((n: string) => <li key={n}>{n}</li>)}
                        </ul>
                    );

                    let body: React.ReactNode;
                    if (hasBoth) {
                        body = (
                            <>
                                {systemDups.length === 1
                                    ? <p>{systemDups[0]} already exists in your system.</p>
                                    : <><p>{systemDups.length} files already exist in your system:</p>{fileList(systemDups)}</>
                                }
                                {listDups.length === 1
                                    ? <p>{listDups[0]} is already in your upload list.</p>
                                    : <><p>{listDups.length} files are already in your upload list:</p>{fileList(listDups)}</>
                                }
                            </>
                        );
                    } else if (systemDups.length > 0) {
                        if (totalBlocked === 1) {
                            body = <p>{systemDups[0]} already exists in your system.</p>;
                        } else if (nonDuplicateCount === 0) {
                            body = <><p>{systemDups.length} files already exist in your system:</p>{fileList(systemDups)}</>;
                        } else {
                            const label = systemDups.length === 1 ? '1 duplicate file was not added:' : `${systemDups.length} duplicate files were not added:`;
                            const footer = systemDups.length === 1 ? 'This file already exists in your system.' : 'These files already exist in your system.';
                            body = <><p>{label}</p>{fileList(systemDups)}<p>{footer}</p></>;
                        }
                    } else {
                        if (totalBlocked === 1) {
                            body = <p>{listDups[0]} is already in your upload list.</p>;
                        } else if (nonDuplicateCount === 0) {
                            body = fileList(listDups);
                        } else {
                            const label = listDups.length === 1 ? '1 duplicate file was not added:' : `${listDups.length} duplicate files were not added:`;
                            const footer = listDups.length === 1 ? 'This file is already in your upload list.' : 'These files are already in your upload list.';
                            body = <><p>{label}</p>{fileList(listDups)}<p>{footer}</p></>;
                        }
                    }

                    return (
                        <div className="upload-dup-warning" role="alert">
                            <WarningFilled size={16} className="upload-dup-warning__icon" />
                            <div className="upload-dup-warning__content">
                                <p className="upload-dup-warning__title">{title}</p>
                                <div className="upload-dup-warning__body">{body}</div>
                            </div>
                            <button
                                type="button"
                                className="upload-dup-warning__close-btn"
                                onClick={() => setDupWarning(null)}
                                aria-label="Dismiss"
                            >
                                <Close size={16} />
                            </button>
                        </div>
                    );
                })()}

                <div
                    ref={dropZoneRef}
                    className={`upload-drop-zone${isDragging ? ' upload-drop-zone--dragging' : ''}${isDragInvalid ? ' upload-drop-zone--invalid' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    aria-label="Drop CSV or XLSX files here"
                >
                    {isDragInvalid ? (
                        <>
                            <ErrorIcon size={48} className="upload-drop-zone__invalid-icon" />
                            <p className="upload-drop-zone__invalid-heading">Unsupported file type</p>
                            <p className="upload-drop-zone__invalid-body">
                                Please upload CSV or XLSX files only.
                            </p>
                        </>
                    ) : (
                        <>
                            <CloudUpload size={48} className="upload-drop-zone__icon" />
                            <p className="upload-drop-zone__heading">
                                {isDragging ? 'Drop files here' : 'Drag and drop files here'}
                            </p>
                            <p className="upload-drop-zone__or">or</p>
                            <button
                                type="button"
                                className="upload-drop-zone__browse-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Browse Files
                            </button>
                            <p className="upload-drop-zone__hint">Accepted file types: .csv, .xlsx</p>
                            <p className="upload-drop-zone__hint">You can upload multiple files at once</p>
                        </>
                    )}
                </div>

                {selectedFiles.length > 0 && (
                    <div className="upload-selected-section">
                        <div className="upload-selected-header">
                            <span className="upload-selected-title">
                                Selected files ({selectedFiles.length})
                            </span>
                            <button
                                type="button"
                                className="upload-add-more-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Add more files +
                            </button>
                            <span className="upload-selected-summary">
                                {validFiles.length} file{validFiles.length !== 1 ? 's' : ''} &bull; {formatBytes(totalSize)} total
                            </span>
                        </div>
                        <ul className="upload-file-list" aria-label="Selected files">
                            {selectedFiles.map(({id, file, error}) => (
                                <li
                                    key={id}
                                    className={`upload-file-item${error ? ' upload-file-item--invalid' : ''}`}
                                >
                                    <XlsxIcon className="upload-file-type-icon" />
                                    <div className="upload-file-item__info">
                                        <span className="upload-file-item__name" title={file.name}>
                                            {file.name}
                                        </span>
                                        {error
                                            ? (
                                                <span className="upload-file-item__error-msg">
                                                    <WarningFilled size={12} />{error}
                                                </span>
                                            )
                                            : (
                                                <span className="upload-file-item__meta">
                                                    {formatBytes(file.size)} &bull; {getFileTypeName(file)}
                                                </span>
                                            )
                                        }
                                    </div>
                                    <div className="upload-file-item__actions">
                                        {!error && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="upload-file-item__replace-btn"
                                                    onClick={() => handleReplace(id)}
                                                >
                                                    Replace
                                                </button>
                                                <span className="upload-file-item__divider" aria-hidden="true">|</span>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className="upload-file-item__remove-btn"
                                            onClick={() => removeFile(id)}
                                            aria-label={`Remove ${file.name}`}
                                        >
                                            <TrashCan size={16} />
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {selectedFiles.length > 1 && (
                            <div className="upload-tip-box upload-tip-box--files">
                                <InformationFilled size={16} className="upload-tip-box__icon" />
                                <p className="upload-tip-box__text">
                                    Files are uploaded and processed one at a time. You'll be notified as each file is ready.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <hr className="upload-section-divider" />

                <div className="upload-requirements">
                    <div className="upload-requirements__header">
                        <div>
                            <p className="upload-requirements__title">File formatting requirements</p>
                            <p className="upload-requirements__subtitle">
                                Make sure your files meet the following requirements to avoid errors.
                            </p>
                        </div>
                        <button type="button" className="upload-requirements__download-btn">
                            Download Template <Download size={16} />
                        </button>
                    </div>
                    <table className="upload-requirements__table">
                        <thead>
                            <tr>
                                <th>Requirement</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {REQUIREMENTS.map(({requirement, details, subRow}) => (
                                <tr key={requirement} className={subRow ? 'upload-requirements__sub-row' : ''}>
                                    <td>{requirement}</td>
                                    <td>{details}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button type="button" className="upload-requirements__docs-link">
                        View full documentation <Launch size={14} />
                    </button>
                    <div className="upload-tip-box">
                        <InformationFilled size={16} className="upload-tip-box__icon" />
                        <p className="upload-tip-box__text">
                            <strong>Tip:</strong> You can download the template to see the correct format and example data.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default UploadFilesModal;
